// web/src/components/ds/DsSideSheet.tsx — DS.7 (sprint aos-ds-settings-modals,
// spec §2 D6b / D8b / D18).
//
// `@ds/overlays/SideSheet` plus the two things it lacks and today's Radix
// `ui/sheet.tsx` has:
//
//   * A FOCUS TRAP. `SideSheet` implements Escape only `[medido: SideSheet.tsx
//     :38-40 — the whole keyboard surface]`, while Radix's `SheetContent`
//     traps Tab. Migrating without one would silently delete a behaviour no
//     test in this repo covers (spec §0.7: Escape/trap/return/scroll-lock are
//     covered by exactly ONE assertion across the whole surface). The trap is
//     the shared `useFocusTrap` hook from DsModal.tsx — one implementation,
//     two consumers. The generic fix is an upstream issue (§6 row 6), never a
//     vendor patch (DS-C4 / D-1084's 3-file threshold).
//   * THE LAYER REGISTRATION (D18). `variant="modal"` scroll-locks the page,
//     so a sheet open under a confirm must not eat that confirm's Escape.
//
// `hideChrome` suppresses the piece's own header row. `DocViewerSheet` renders
// its own header — the path chip, the download link and a close button, three
// of the eleven `data-testid` anchors AC-12 pins — so the piece's title+✕ row
// would be a SECOND close affordance stacked above it. The suppression is one
// CSS rule in ds-adapters.css keyed on the class below (the same shape as
// DS.5's ripple suppression, D6b), because the header is the piece's own DOM
// and no prop turns it off. It targets `.sheet`'s FIRST child, which the piece
// renders unconditionally as the header (SideSheet.tsx:70-75).

import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  type ComponentProps,
} from 'react';
import SideSheet from '@ds/overlays/SideSheet';
import { useAriaHideOutside, useFocusTrap, useOverlayLayer } from './DsModal';
import './ds-adapters.css';

export interface DsSideSheetProps extends ComponentProps<typeof SideSheet> {
  /** Hide the piece's own header row (title + ✕) — the host renders one. */
  hideChrome?: boolean;
}

export const DsSideSheet = forwardRef<HTMLDivElement, DsSideSheetProps>(function DsSideSheet(
  { open, onClose, hideChrome = false, className = '', ...rest },
  ref,
) {
  const sheetRef = useRef<HTMLDivElement>(null);
  useImperativeHandle(ref, () => sheetRef.current as HTMLDivElement, []);

  const isTopmost = useOverlayLayer(open);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // D18: evaluated at EVENT time, never at render.
  const handleClose = useCallback(() => {
    if (isTopmost()) onCloseRef.current();
  }, [isTopmost]);

  useFocusTrap(sheetRef, open);
  useAriaHideOutside(sheetRef, open);

  const classes = [hideChrome ? 'aos-ds-side-sheet--no-chrome' : '', className]
    .filter(Boolean)
    .join(' ');

  return <SideSheet ref={sheetRef} open={open} onClose={handleClose} className={classes} {...rest} />;
});

export default DsSideSheet;
